import { call, delay, fork, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { reduce, some } from 'lodash-es';
import { replace, push } from 'redux-first-history';
import { stringify } from 'query-string';
import { ExportStatus, TaskType, Export } from '@dev/ee-api/ee-api-models';

import {
    CancelPollingExports,
    DeleteExportError,
    DeleteExportStart,
    DeleteExportSuccess,
    ExportContactsError,
    ExportContactsSuccess,
    LoadExports,
    LoadingExportsError,
    LoadingExportsSuccess,
    StartLoadingExports,
    StartPollingExports,
} from '@/store/exports/actions';
import {
    BeginPollingExportsAction,
    DeleteExportsAction,
    ExportContactsStartAction,
    FilterExportsAction,
    LoadExportsAction,
    SearchExportsAcion,
    INITIAL_PER_PAGE,
} from '@/store/exports/types';
import { deleteTask, exportContacts, loadTasks } from '@/services/ElasticApi';
import { HideToast, ShowToast } from '@/store/toast/actions';
import { ApplicationState as State, getRouterStore } from '@/store';
import { ExportsPaths } from '@/services/dictionaries/Paths';
import { parseUrlParams } from '@/services/tools/handleUrlParams';
import { Logger } from '@/services/errors/appLogger';

type StoreSliceInterface = {
    activePage: number;
    type: TaskType;
    exportsPerPage: number;
    search: string;
    exportsList: Export[];
};

const getParamsFromExportStore = (state: State): StoreSliceInterface => ({
    activePage: state?.exports?.activePage,
    type: state?.exports?.type,
    exportsPerPage: state?.exports?.exportsPerPage,
    search: state?.exports.search,
    exportsList: state?.exports?.exportsList,
});

export const getLimitAndOffset = (activePage: number, exportsPerPage: number): { offset: number; limit: number } => {
    const limit = exportsPerPage;
    const offset = exportsPerPage * activePage - exportsPerPage;

    return { limit, offset };
};
const Headers = {
    'x-total-count': 'totalCount',
    'x-total-ready-count': 'readyCount',
    'x-total-loading-count': 'loadingCount',
    'x-total-verified-count': 'verifiedCount',
    'x-total-expired-count': 'expiredCount',
    'x-total-error-count': 'errorCount',
    'x-total-campaign-count': 'campaingCount',
    'x-total-contact-count': 'contactCount',
    'x-total-linktracking-count': 'linktrackingCount',
    'x-total-log-count': 'logCount',
    'x-total-survey-count': 'surveyCount',
    'x-total-unsubscribereason-count': 'unsubscribereasonCount',
    'x-total-singleemailverification-count': 'singleemailverificationCount',
    'x-total-emailverificationlist-count': 'emailverificationlistCount',
    'x-total-linkcontacts-count': 'linkcontactsCount',
    'x-total-emailverification-count': 'emailverificationCount',
    'x-total-channel-count': 'channelCount',
};

const parseHeaders = (headers: any): any =>
    reduce(
        Object.keys(Headers),
        (o, item) => {
            if (headers[item]) {
                return { ...o, [Headers[item]]: parseInt(headers[item], 10) || 0 };
            }
            return o;
        },
        {}
    );

function* startExport({ payload }: ExportContactsStartAction): any {
    try {
        const contacts = yield call(exportContacts, payload.params);
        yield put(ExportContactsSuccess(contacts));
        yield put(push(ExportsPaths.Exports));
    } catch (e) {
        yield put(ExportContactsError(e));
        Logger.error(e?.response?.data?.Error);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* loadExports(action: LoadExportsAction | FilterExportsAction | SearchExportsAcion): any {
    yield put(CancelPollingExports());

    const { payload = {} } = action;
    const exportsStore: StoreSliceInterface = yield select(getParamsFromExportStore);
    const getLimitOffset = getLimitAndOffset(
        !payload?.activePage ? exportsStore?.activePage : payload?.activePage,
        !payload?.exportsPerPage ? exportsStore?.exportsPerPage : payload?.exportsPerPage
    );
    const getParams = {
        statuses: payload.statuses ? payload.statuses : [],
        types: payload.types ? payload.types : [],
        search: payload.search ? payload.search : null,
        ...getLimitOffset,
    };

    if (action.type === '@@exports/FILTER_EXPORTS' || action.type === '@@exports/SEARCH_EXPORTS') {
        yield delay(500);
    }
    const exportsParams = { type: exportsStore.type, ...getParams };
    if (!payload.supressLoadIndicator) {
        yield put(StartLoadingExports());
    }

    try {
        const resp = yield call(loadTasks, exportsParams);
        const counts = parseHeaders(resp.headers);

        const exportsList = resp.data;

        if (some(exportsList, { ExportStatus: ExportStatus.Loading }) && !exportsParams?.offset) {
            yield put(StartPollingExports(exportsParams));
        }

        yield put(LoadingExportsSuccess(exportsList, counts));
    } catch (e) {
        yield put(LoadingExportsError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );

        Logger.error(e?.response?.data?.Error);
    }
}

function* deleteExports(action: DeleteExportsAction): any {
    const { payload } = action;
    const { exportsList, activePage } = yield select(getParamsFromExportStore);
    const { location } = yield select(getRouterStore);
    yield put(DeleteExportStart());

    try {
        const query = parseUrlParams(location.search);
        const { perPage = INITIAL_PER_PAGE, sortBy, search, types, statuses } = query;
        const newActivePage = exportsList.length === 1 && activePage > 1 ? activePage - 1 : activePage;
        yield call(deleteTask, payload.id);
        const queryParams = { page: newActivePage, perPage, sortBy, search, types, statuses };
        const params = {
            exportsPerPage: +perPage,
            activePage: newActivePage,
            statuses,
            search,
            types,
        };
        yield put(replace({ search: stringify(queryParams) }));
        yield put(DeleteExportSuccess());
        yield put(
            ShowToast({
                severity: 'info',
                message: `Export deleted`,
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield delay(200);
        yield put(LoadExports(params));
    } catch (e) {
        yield put(DeleteExportError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* pollingLoadExports(action?: BeginPollingExportsAction): any {
    while (true) {
        const { payload = {} } = action;
        const exportsStore: StoreSliceInterface = yield select(getParamsFromExportStore);
        const getLimitOffset = getLimitAndOffset(
            payload?.activePage ?? exportsStore?.activePage,
            payload?.exportsPerPage ?? exportsStore?.exportsPerPage
        );
        const getParams = {
            statuses: payload.statuses ? payload.statuses : [],
            types: payload.types ? payload.types : [],
            search: payload.search,
            ...getLimitOffset,
        };
        const exportsParams = { type: exportsStore.type, ...getParams };

        try {
            yield delay(5000);
            const resp = yield call(loadTasks, exportsParams);
            const counts = parseHeaders(resp.headers);

            const exportsList = resp.data;
            yield put(LoadingExportsSuccess(exportsList, counts));

            if (!some(exportsList, { ExportStatus: ExportStatus.Loading })) {
                yield put(CancelPollingExports());
            }
        } catch (e) {
            yield put(LoadingExportsError());
            yield put(
                ShowToast({
                    severity: 'error',
                    message: `Error: ${e?.response?.data?.Error}`,
                    duration: 6000,
                    onClose: HideToast,
                })
            );

            Logger.error(e?.response?.data?.Error);
        }
    }
}

function* watchPollingLoadExports(): any {
    while (true) {
        const pollAction = yield take('@@exports/BEGIN_POLLING_EXPORTS');
        yield race([call(pollingLoadExports, pollAction), take('@@exports/CANCEL_POLLING_EXPORTS')]);
    }
}

function* watchStartExport(): any {
    yield takeLatest('@@export/CONTACTS_START', startExport);
}

function* watchLoadExports(): any {
    yield takeLatest('@@exports/FETCH_EXPORTS', loadExports);
}

function* watchSearchExports(): any {
    yield takeLatest('@@exports/SEARCH_EXPORTS', loadExports);
}

function* watchFilterExports(): any {
    yield takeEvery('@@exports/FILTER_EXPORTS', loadExports);
}

function* watchDeleteExport(): any {
    yield takeEvery('@@exports/DELETE_EXPORTS', deleteExports);
}

const exportSagas = [
    fork(watchStartExport),
    fork(watchLoadExports),
    fork(watchFilterExports),
    fork(watchDeleteExport),
    fork(watchSearchExports),
    fork(watchPollingLoadExports),
];

export default exportSagas;
